import classNames from 'classnames';
import {Pagination} from 'swiper';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Image} from '@shopify/hydrogen';
import 'swiper/swiper-bundle.min.css';

import {interpolate} from 'utils/i18n';

import {useI18n} from '../../contexts/I18n.client';
import {Container} from '../Container.client';

import affiliateImgEn from './assets/affiliate-en.png';
import affiliateImgInt from './assets/affiliate-int.png';
import dropshipImgEn from './assets/dropship-en.png';
import dropshipImgInt from './assets/dropship-int.png';
import merchImg from './assets/merch.png';
import arrowSvg from './assets/arrow.svg';
import {useIsMobile} from 'hooks/useIsMobile';

type Cards = Array<{
  key: string;
  image: string;
}>;

type MonetizeProps = {
  className?: string;
};

export const Monetize = (props: MonetizeProps) => {
  const {t, language} = useI18n();

  const cards = [
    {key: 'merch', image: merchImg},
    {
      key: 'dropship',
      image: language === 'en' ? dropshipImgEn : dropshipImgInt,
    },
    {
      key: 'affiliate',
      image: language === 'en' ? affiliateImgEn : affiliateImgInt,
    },
  ];

  const isMobile = useIsMobile();

  return (
    <Container
      className={props.className}
      backgroundGradientStart="white"
      backgroundGradientEnd="white"
    >
      <div
        className={classNames('m-auto', 'text-center', 'w-auto', 'md:w-[80%]')}
      >
        <p
          className={classNames(
            'font-bold',
            'leading-[110%]',
            'text-[36px]',
            'mb-4',
            'md:text-[48px]',
            'md:mb-6',
            'lg:text-[60px]',
          )}
        >
          {t('monetize.title')}
        </p>
        <p
          className={classNames(
            'text-[20px]',
            'leading-[150%]',
            'mb-16',
            'md:mb-20',
          )}
        >
          {interpolate(t('monetize.body'), {
            starterlink: (content) => (
              <a
                className={classNames(
                  'font-bold',
                  'underline',
                  'whitespace-nowrap',
                )}
                href={t('monetize.ctaUrl')}
                target="_blank"
                rel="noreferrer"
              >
                {content}
              </a>
            ),
          })}
        </p>
      </div>

      {isMobile ? (
        <CardsCarousel cards={cards} />
      ) : (
        <CardsStatic cards={cards} />
      )}
    </Container>
  );
};

const CardsCarousel = ({cards}: {cards: Cards}) => {
  const {t} = useI18n();

  return (
    <Swiper
      className={classNames('!-mx-4', 'md:!mx-auto')}
      modules={[Pagination]}
      slidesPerView={1.1}
      loop
      pagination={{
        type: 'custom',
        renderCustom: (_, active, total) => {
          const slides = Array(total)
            .fill(null)
            .map((_, i) => i + 1);

          const bullets = slides
            .map(
              (current) => `
                <div class="${classNames('h-[2px]', 'flex-1', 'bg-stone-300', {
                  '!h-1': current === active,
                  'bg-blue-brochure': current === active,
                })}"></div>`,
            )
            .join('');

          return `
            <div class="${classNames(
              'flex',
              'gap-4',
              'w-[70%]',
              'mx-4',
              'items-center',
            )}">
              ${bullets}
            </div>
          `;
        },
        horizontalClass: classNames(
          // Sets the `top` to a value calculated to be the height of the square image
          'top-[calc((100vw_/_1.1)_-_16px)]',
          'mt-[40px]',
          '!bottom-auto',
          'pointer-events-none',
        ),
      }}
    >
      {cards.map(({key, image}) => (
        <SwiperSlide
          key={key}
          className={classNames(
            '!h-auto',
            'pl-4',
            'flex',
            'flex-col',
            'bg-white',
          )}
        >
          <Image
            className={classNames('w-full', 'mb-[84px]')}
            src={`${image}?width=370&height=370`}
            srcSet={`${image}?width=370&height=370, ${image}?width=740&height=740 2x, ${image}?width=1110&height=1110 3x`}
            width={370}
            height={370}
            alt={key}
          />

          <p
            className={classNames(
              'font-bold',
              'text-[26px]',
              'leading-[120%]',
              'text-blue-brochure',
              'mb-2',
            )}
          >
            {t(`monetize.methods.${key}.title`)}
          </p>

          <p className={classNames('text-[20px]', 'leading-[150%]', 'mb-12')}>
            {t(`monetize.methods.${key}.body`)}
          </p>

          <a
            className={classNames(
              'mt-auto',
              'font-bold',
              'text-[16px]',
              'leading-[150%]',
              'text-blue-brochure',
              'underline',
            )}
            href={t(`monetize.methods.${key}.ctaUrl`)}
            target="_blank"
            rel="noreferrer"
          >
            {t(`monetize.methods.${key}.cta`)}
            <Image
              className={classNames('inline', 'ml-2')}
              src={arrowSvg}
              width={15}
              height={15}
              alt="right arrow"
            />
          </a>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const CardsStatic = ({cards}: {cards: Cards}) => {
  const {t} = useI18n();

  return (
    <div className={classNames('flex', 'gap-6')}>
      {cards.map(({key, image}) => (
        <div
          key={key}
          className={classNames(
            'flex-1',
            'shadow-10px',
            'border',
            'border-black',
            'rounded-[18px]',
            'p-4',
            'lg:p-8',
            'flex',
            'flex-col',
            'bg-white',
          )}
        >
          <Image
            className={classNames('w-full', 'mb-[40px]', 'rounded-md')}
            src={`${image}?width=370&height=370`}
            srcSet={`${image}?width=370&height=370, ${image}?width=740&height=740 2x, ${image}?width=1110&height=1110 3x`}
            width={370}
            height={370}
            alt={key}
          />

          <p
            className={classNames(
              'font-bold',
              'text-[30px]',
              'leading-[120%]',
              'mb-4',
            )}
          >
            {t(`monetize.methods.${key}.title`)}
          </p>

          <p className={classNames('text-[20px]', 'leading-[150%]', 'mb-6')}>
            {t(`monetize.methods.${key}.body`)}
          </p>

          <a
            className={classNames(
              'mt-auto',
              'font-bold',
              'text-[20px]',
              'leading-[150%]',
              'text-blue-brochure',
              'underline',
            )}
            href={t(`monetize.methods.${key}.ctaUrl`)}
            target="_blank"
            rel="noreferrer"
          >
            {t(`monetize.methods.${key}.cta`)}
            <Image
              className={classNames('inline', 'ml-[3px]')}
              src={arrowSvg}
              width={15}
              height={15}
              alt="right arrow"
            />
          </a>
        </div>
      ))}
    </div>
  );
};
